import React, { useState, useCallback } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';

interface SizeObject {
  id: string;
  name: string;
  size: 'small' | 'medium' | 'large';
  length: number;
  color: string;
  emoji: string;
}

interface SizeComparisonConfigurationFormProps {
  config: any;
  onChange: (config: any) => void;
}

const SizeComparisonConfigurationForm: React.FC<SizeComparisonConfigurationFormProps> = ({
  config,
  onChange
}) => {
  const [localConfig, setLocalConfig] = useState({
    name: config?.name || 'Comparaison de Tailles',
    title: config?.title || 'Comparaison de Tailles',
    description: config?.description || 'Comparez les objets et classez-les par taille',
    instructions: config?.instructions || 'Glissez les objets pour les ranger du plus petit au plus grand',
    showMeasurements: config?.showMeasurements ?? true,
    allowDrag: config?.allowDrag ?? true,
    objects: config?.objects || [
      {
        id: 'obj1',
        name: 'Crayon court',
        size: 'small',
        length: 8,
        color: 'red',
        emoji: '✏️'
      },
      {
        id: 'obj2',
        name: 'Crayon long',
        size: 'large',
        length: 15,
        color: 'blue',
        emoji: '✏️'
      }
    ]
  });

  const handleConfigChange = useCallback((updates: any) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  }, [localConfig, onChange]);

  const addObject = useCallback(() => {
    const newObject: SizeObject = {
      id: `obj${Date.now()}`,
      name: 'Nouvel objet',
      size: 'medium',
      length: 10,
      color: 'green',
      emoji: '📦'
    };
    
    handleConfigChange({
      objects: [...localConfig.objects, newObject]
    });
  }, [localConfig.objects, handleConfigChange]);

  const removeObject = useCallback((objectId: string) => {
    handleConfigChange({
      objects: localConfig.objects.filter((obj: SizeObject) => obj.id !== objectId)
    });
  }, [localConfig.objects, handleConfigChange]);

  const updateObject = useCallback((objectId: string, updates: Partial<SizeObject>) => {
    handleConfigChange({
      objects: localConfig.objects.map((obj: SizeObject) =>
        obj.id === objectId ? { ...obj, ...updates } : obj
      )
    });
  }, [localConfig.objects, handleConfigChange]);

  const sizeOptions = [
    { value: 'small', label: 'Petit' },
    { value: 'medium', label: 'Moyen' },
    { value: 'large', label: 'Grand' }
  ];

  const colorOptions = [
    { value: 'red', label: 'Rouge' },
    { value: 'blue', label: 'Bleu' },
    { value: 'green', label: 'Vert' },
    { value: 'yellow', label: 'Jaune' },
    { value: 'purple', label: 'Violet' },
    { value: 'orange', label: 'Orange' },
    { value: 'pink', label: 'Rose' },
    { value: 'brown', label: 'Marron' }
  ];

  const emojiOptions = [
    '✏️', '🖊️', '📏', '📐', '🧽', '📦', '📚', '🔍', '📱', '⌚',
    '🎯', '🏀', '⚽', '🎲', '🧸', '🚗', '✈️', '🚀', '🏠', '🌳'
  ];

  return (
    <div className="space-y-6">
      {/* Configuration de base */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Configuration de base</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom de la ressource
          </label>
          <input
            type="text"
            value={localConfig.name}
            onChange={(e) => handleConfigChange({ name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titre affiché
          </label>
          <input
            type="text"
            value={localConfig.title}
            onChange={(e) => handleConfigChange({ title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={localConfig.description}
            onChange={(e) => handleConfigChange({ description: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Instructions
          </label>
          <textarea
            value={localConfig.instructions}
            onChange={(e) => handleConfigChange({ instructions: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={localConfig.showMeasurements}
              onChange={(e) => handleConfigChange({ showMeasurements: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Afficher les mesures</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={localConfig.allowDrag}
              onChange={(e) => handleConfigChange({ allowDrag: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Permettre le glisser-déposer</span>
          </label>
        </div>
      </div>

      {/* Configuration des objets */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Objets à comparer</h3>
          <button
            type="button"
            onClick={addObject}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Ajouter un objet
          </button>
        </div>

        <div className="space-y-3">
          {localConfig.objects.map((obj: SizeObject, index: number) => (
            <div key={obj.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <GripVertical className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Objet {index + 1}</span>
                <button
                  type="button"
                  onClick={() => removeObject(obj.id)}
                  className="ml-auto p-1 text-red-600 hover:bg-red-100 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={obj.name}
                    onChange={(e) => updateObject(obj.id, { name: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Taille
                  </label>
                  <select
                    value={obj.size}
                    onChange={(e) => updateObject(obj.id, { size: e.target.value as 'small' | 'medium' | 'large' })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {sizeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Longueur (cm)
                  </label>
                  <input
                    type="number"
                    value={obj.length}
                    onChange={(e) => updateObject(obj.id, { length: parseInt(e.target.value) || 0 })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    min="1"
                    max="100"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Couleur
                  </label>
                  <select
                    value={obj.color}
                    onChange={(e) => updateObject(obj.id, { color: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {colorOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Emoji
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {emojiOptions.map(emoji => (
                      <button
                        type="button"
                        key={emoji}
                        onClick={() => updateObject(obj.id, { emoji })}
                        className={`w-8 h-8 text-lg rounded border-2 transition-colors ${
                          obj.emoji === emoji
                            ? 'border-blue-500 bg-blue-100'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {localConfig.objects.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>Aucun objet configuré. Cliquez sur "Ajouter un objet" pour commencer.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export { SizeComparisonConfigurationForm };
export default SizeComparisonConfigurationForm;
